import Cta from "@components/Cta/Cta";
import { NextContentfulImage } from "@components/Image";
import Lightbox, { LightboxRefProps } from "@components/Lightbox/Lightbox";
import { fetchApi } from "@utilities/fetchApi";
import { useEffect, useRef, useState } from "react";
import videoIcon from "../../../public/assets2019/img/vidicon.png";
import { Results } from "../CoveoSearchComponent";
import FacultyProfileSolo from "../ResearchAndFacultyResultsTemplate/ResearchAndFacultyProfileSolo";

interface ResearchAndFacultyResultsTemplateProps {
  result: Results;
  priority?: boolean;
  handleSelect?: () => void;
}

export const ResearchAndFacultyResultsTemplate = (
  props: ResearchAndFacultyResultsTemplateProps
) => {
  const { result, priority = false } = props;

  const lightboxRef = useRef<LightboxRefProps | undefined>(undefined);
  const [showLightbox, setShowLightbox] = useState(false);
  const [researchDetails, setResearchDetails] = useState<any>();

  const closeLightbox = () => {
    setTimeout(() => {
      setShowLightbox(false);
    });
  };

  const extractPathWithoutLeadingSlash = (url) => {
    const regex = /^(?:https?:\/\/)?[^\/]+(\/.*)$/;
    const match = url.match(regex);
    if (match) {
      // Remove the first slash if it exists.
      return match[1].startsWith("/") ? match[1].slice(1) : match[1];
    }
    return "";
  };

  const slug = extractPathWithoutLeadingSlash(result.ClickUri);

  useEffect(() => {
    const fetchResearchDetails = async () => {
      try {
        const researchDetailsResponse = await fetchApi(
          "/api/getResearchDetailsBySlug",
          {
            slug,
          }
        );

        const data = {
          researchTitle: researchDetailsResponse?.researchTitle,
          authors: researchDetailsResponse?.authorsListCollection?.items,
          imageData: researchDetailsResponse?.thumbnailImage,
          longDescription: researchDetailsResponse?.longDescription?.json,
          longDescriptionPartTwo:
            researchDetailsResponse?.longDescriptionPartTwo?.json,
          relatedResearchLink: researchDetailsResponse?.cta,
          videoData: researchDetailsResponse?.video,
        };

        if (data.videoData) {
          data.videoData.isAutoPlay = true;

          if (data.videoData.overlayImage) {
            data.videoData.overlayImage.fields = {
              url: researchDetailsResponse?.video?.overlayImage?.image?.url,
              altText: researchDetailsResponse?.video?.overlayImage?.altText,
            };
          }
        }
        setResearchDetails(data);
      } catch (error) {
        console.error("Error fetching research details:", error);
      }
    };
    fetchResearchDetails();
  }, []);

  const researchPageTitle = result?.raw?.researchtitle;
  const imageUrl = result?.raw?.articleimageurl;

  const description = result.raw?.pagedescription || "";

  return (
    <div className="research-faculty-tile">
      <a
        href={null}
        onClick={() => {
          setShowLightbox(true);
          if (props.handleSelect) {
            props.handleSelect();
          }
        }}
        className="research-faculty-image-container"
      >
        {imageUrl && (
          <NextContentfulImage
            src={imageUrl}
            alt={researchPageTitle}
            width={400}
            height={250}
            sizes="20vw"
            {...(priority ? { priority: true, loading: "eager" } : {})}
          />
        )}

        {researchDetails?.videoData && (
          <div className="play-icon">
            <NextContentfulImage
              src={videoIcon}
              alt="play icon"
              width={160}
              height={160}
            />
          </div>
        )}
      </a>

      <div className="research-faculty-content">
        <h4 className="research-faculty-title">
          <a
            onClick={() => {
              setShowLightbox(true);
              if (props.handleSelect) {
                props.handleSelect();
              }
            }}
            href={null}
          >
            {researchPageTitle}
          </a>
        </h4>

        <p className="research-faculty-description">{description}</p>

        <Cta
          ariaLabel={`Find out more about ${researchPageTitle}`}
          linkText="Find out more"
          type="tertiary"
          url={null}
          icon="icon-arrow"
          onClick={() => {
            setShowLightbox(true);
          }}
        />
      </div>
      {showLightbox && (
        <Lightbox
          ref={lightboxRef}
          onCloseFunction={closeLightbox}
          customInnerClass="faculty-profile-carousel"
        >
          {researchDetails && (
            <FacultyProfileSolo
              content={researchDetails}
              lightbox={true}
              onCloseFunction={() => lightboxRef.current.close()}
            />
          )}
        </Lightbox>
      )}
    </div>
  );
};

export default ResearchAndFacultyResultsTemplate;
